/**
 * Equipment Check Page - Hardware verification before video sessions
 * Tests camera, microphone, and speaker without requiring authentication or server connection.
 * Required per BMV-Ä Anlage 31c for pre-session technical verification.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Badge,
  BackgroundPicker,
  useBackgroundProcessor,
  useVideoBackgrounds,
  saveDevicePreference,
  getPreferredDeviceId,
} from "@jourfx/ui";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { createLocalVideoTrack, LocalVideoTrack } from "livekit-client";
import {
  Camera,
  CameraOff,
  Mic,
  MicOff,
  Volume2,
  Monitor,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ArrowLeft,
} from "lucide-react";

export const Route = createFileRoute("/equipment-check")({
  component: EquipmentCheckPage,
});

type DeviceStatus = "pending" | "checking" | "ok" | "error" | "warning";

interface DeviceCheckResult {
  status: DeviceStatus;
  message: string;
  deviceLabel?: string;
}

function EquipmentCheckPage() {
  // Device lists
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);

  // Selected devices
  const [selectedCamera, setSelectedCamera] = useState<string>("");
  const [selectedMic, setSelectedMic] = useState<string>("");
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>("");

  // Check results
  const [cameraResult, setCameraResult] = useState<DeviceCheckResult>({
    status: "pending",
    message: "Nicht geprüft",
  });
  const [micResult, setMicResult] = useState<DeviceCheckResult>({
    status: "pending",
    message: "Nicht geprüft",
  });
  const [speakerResult, setSpeakerResult] = useState<DeviceCheckResult>({
    status: "pending",
    message: "Nicht geprüft",
  });
  const [browserResult, setBrowserResult] = useState<DeviceCheckResult>({
    status: "pending",
    message: "Nicht geprüft",
  });

  // LiveKit video track (replaces raw getUserMedia for camera)
  const [videoTrack, setVideoTrack] = useState<LocalVideoTrack | null>(null);

  // Audio level
  const [audioLevel, setAudioLevel] = useState(0);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number>(0);

  // Background effects (standalone mode - no Room needed)
  const adminBackgrounds = useQuery(api.admin.videoBackgrounds.listPublic);
  const presets = useVideoBackgrounds(adminBackgrounds);
  const background = useBackgroundProcessor({
    track: videoTrack,
    presets,
  });

  // Cleanup stream
  const stopStream = useCallback(() => {
    // Stop LiveKit video track
    if (videoTrack) {
      videoTrack.detach();
      videoTrack.stop();
      setVideoTrack(null);
    }
    // Audio stream cleanup (mic check still uses raw getUserMedia)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, [videoTrack]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (videoTrack) {
        videoTrack.detach();
        videoTrack.stop();
      }
      stopStream();
    };
  }, [videoTrack, stopStream]);

  // Check browser compatibility
  useEffect(() => {
    setBrowserResult({ status: "checking", message: "Prüfe Browser..." });

    const checks: string[] = [];
    let status: DeviceStatus = "ok";

    if (!navigator.mediaDevices?.getUserMedia) {
      setBrowserResult({
        status: "error",
        message: "Browser unterstützt keine Medienzugriffe (getUserMedia fehlt)",
      });
      return;
    }

    if (!window.RTCPeerConnection) {
      checks.push("WebRTC nicht verfügbar");
      status = "error";
    }

    if (!window.MediaStream) {
      checks.push("MediaStream nicht verfügbar");
      status = "error";
    }

    // Check for secure context (required for getUserMedia)
    if (!window.isSecureContext) {
      checks.push("Kein sicherer Kontext (HTTPS erforderlich)");
      status = "warning";
    }

    if (status === "ok") {
      setBrowserResult({
        status: "ok",
        message: "Browser ist kompatibel",
      });
    } else {
      setBrowserResult({
        status,
        message: checks.join("; "),
      });
    }
  }, []);

  // Enumerate devices
  const enumerateDevices = useCallback(async () => {
    try {
      // Need to request permission first to get device labels
      const tempStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
      tempStream.getTracks().forEach((t) => t.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === "videoinput");
      const mics = devices.filter((d) => d.kind === "audioinput");
      const spks = devices.filter((d) => d.kind === "audiooutput");

      setCameras(cams);
      setMicrophones(mics);
      setSpeakers(spks);

      if (cams.length > 0 && !selectedCamera) {
        setSelectedCamera(getPreferredDeviceId("camera", cams.map((d) => d.deviceId)));
      }
      if (mics.length > 0 && !selectedMic) {
        setSelectedMic(getPreferredDeviceId("microphone", mics.map((d) => d.deviceId)));
      }
      if (spks.length > 0 && !selectedSpeaker) {
        setSelectedSpeaker(getPreferredDeviceId("speaker", spks.map((d) => d.deviceId)));
      }

      return { cams, mics, spks };
    } catch (err) {
      const msg =
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Zugriff auf Kamera/Mikrofon wurde verweigert. Bitte Berechtigung erteilen."
          : `Geräte konnten nicht aufgelistet werden: ${err instanceof Error ? err.message : "Unbekannter Fehler"}`;

      setCameraResult({ status: "error", message: msg });
      setMicResult({ status: "error", message: msg });
      return null;
    }
  }, [selectedCamera, selectedMic, selectedSpeaker]);

  // Check camera
  const checkCamera = useCallback(
    async (deviceId?: string) => {
      setCameraResult({ status: "checking", message: "Kamera wird geprüft..." });

      // Stop previous video track
      if (videoTrack) {
        videoTrack.detach();
        videoTrack.stop();
        setVideoTrack(null);
      }
      // Also clear the video element srcObject (in case old raw stream is there)
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      try {
        const track = await createLocalVideoTrack(
          deviceId ? { deviceId: { exact: deviceId } } : undefined
        );

        setVideoTrack(track);

        // Attach to video element
        if (videoRef.current) {
          track.attach(videoRef.current);
        }

        const settings = track.mediaStreamTrack.getSettings();
        const label = track.mediaStreamTrack.label || "Unbekannte Kamera";
        const resolution =
          settings.width && settings.height
            ? `${settings.width}x${settings.height}`
            : "unbekannt";

        setCameraResult({
          status: "ok",
          message: `Kamera funktioniert (${resolution})`,
          deviceLabel: label,
        });
      } catch (err) {
        const msg =
          err instanceof DOMException && err.name === "NotAllowedError"
            ? "Kamerazugriff verweigert. Bitte Berechtigung in den Browsereinstellungen erteilen."
            : err instanceof DOMException && err.name === "NotFoundError"
              ? "Keine Kamera gefunden."
              : `Kamerafehler: ${err instanceof Error ? err.message : "Unbekannt"}`;

        setCameraResult({ status: "error", message: msg });
      }
    },
    [videoTrack]
  );

  // Check microphone
  const checkMicrophone = useCallback(
    async (deviceId?: string) => {
      setMicResult({ status: "checking", message: "Mikrofon wird geprüft..." });

      // Stop previous audio analysis
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      try {
        const constraints: MediaStreamConstraints = {
          audio: deviceId
            ? { deviceId: { exact: deviceId } }
            : true,
          video: false,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        // Add audio tracks to existing stream or create new one
        if (streamRef.current) {
          // Remove old audio tracks
          streamRef.current.getAudioTracks().forEach((t) => t.stop());
          // Add new audio track
          stream.getAudioTracks().forEach((t) => streamRef.current!.addTrack(t));
        }

        const audioTrack = stream.getAudioTracks()[0];
        if (!audioTrack) {
          setMicResult({ status: "error", message: "Kein Audiosignal erkannt" });
          return;
        }

        // Set up audio level monitoring
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyserRef.current = analyser;
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateLevel = () => {
          analyser.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          const normalized = Math.min(100, Math.round((avg / 128) * 100));
          setAudioLevel(normalized);
          animFrameRef.current = requestAnimationFrame(updateLevel);
        };
        updateLevel();

        const label = audioTrack.label || "Unbekanntes Mikrofon";
        setMicResult({
          status: "ok",
          message: "Mikrofon funktioniert. Sprechen Sie, um den Pegel zu testen.",
          deviceLabel: label,
        });
      } catch (err) {
        const msg =
          err instanceof DOMException && err.name === "NotAllowedError"
            ? "Mikrofonzugriff verweigert. Bitte Berechtigung erteilen."
            : err instanceof DOMException && err.name === "NotFoundError"
              ? "Kein Mikrofon gefunden."
              : `Mikrofonfehler: ${err instanceof Error ? err.message : "Unbekannt"}`;

        setMicResult({ status: "error", message: msg });
      }
    },
    []
  );

  // Check speaker
  const checkSpeaker = useCallback(async (deviceId?: string) => {
    setSpeakerResult({ status: "checking", message: "Lautsprecher wird geprüft..." });

    try {
      const audio = new Audio();
      // Use a short beep via oscillator
      const ctx = new AudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(440, ctx.currentTime);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Try to set output device if supported
      if (deviceId && "setSinkId" in audio) {
        try {
          await (audio as any).setSinkId(deviceId);
        } catch {
          // setSinkId might not work in all browsers
        }
      }

      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.5);

      setSpeakerResult({
        status: "ok",
        message: "Testton wurde abgespielt. Haben Sie ihn gehört?",
      });

      // Close audio context after playback
      setTimeout(() => ctx.close(), 600);
    } catch {
      setSpeakerResult({
        status: "warning",
        message: "Lautsprechertest konnte nicht durchgeführt werden. Prüfen Sie die Lautstärke manuell.",
      });
    }
  }, []);

  // Run all checks
  const runAllChecks = useCallback(async () => {
    setCameraResult({ status: "checking", message: "Wird geprüft..." });
    setMicResult({ status: "checking", message: "Wird geprüft..." });
    setSpeakerResult({ status: "checking", message: "Wird geprüft..." });
    setAudioLevel(0);
    stopStream();

    const devices = await enumerateDevices();
    if (!devices) return;

    await checkCamera(selectedCamera || undefined);
    await checkMicrophone(selectedMic || undefined);
    await checkSpeaker(selectedSpeaker || undefined);
  }, [enumerateDevices, checkCamera, checkMicrophone, checkSpeaker, selectedCamera, selectedMic, selectedSpeaker, stopStream]);

  // Auto-run on mount
  useEffect(() => {
    runAllChecks();
  }, [runAllChecks]);

  const allOk =
    cameraResult.status === "ok" &&
    micResult.status === "ok" &&
    browserResult.status === "ok";

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="border-b bg-white dark:bg-stone-900 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={import.meta.env.VITE_ENABLE_VIDEO_DEBUG === "true" ? "/video-debug" : "/dashboard"}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-semibold">Geräteprüfung</h1>
              <p className="text-sm text-muted-foreground">
                Prüfen Sie Kamera, Mikrofon und Lautsprecher vor der Sitzung
              </p>
            </div>
          </div>
          <Button onClick={runAllChecks} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Erneut prüfen
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Overall Status */}
        {allOk && (
          <div className="rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-4 flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-200">
                Alle Geräte funktionieren
              </p>
              <p className="text-sm text-green-600 dark:text-green-400">
                Sie können an einer Video-Sitzung teilnehmen.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Camera Check */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {cameraResult.status === "ok" ? (
                    <Camera className="h-5 w-5 text-green-600" />
                  ) : cameraResult.status === "error" ? (
                    <CameraOff className="h-5 w-5 text-red-600" />
                  ) : (
                    <Camera className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <CardTitle className="text-lg">Kamera</CardTitle>
                    {cameraResult.deviceLabel && (
                      <CardDescription>{cameraResult.deviceLabel}</CardDescription>
                    )}
                  </div>
                </div>
                <StatusBadge status={cameraResult.status} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Camera preview */}
              <div className="relative aspect-video bg-stone-900 rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                {cameraResult.status !== "ok" && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white/70">
                      {cameraResult.status === "checking" ? (
                        <RefreshCw className="h-8 w-8 mx-auto mb-2 animate-spin" />
                      ) : cameraResult.status === "error" ? (
                        <CameraOff className="h-8 w-8 mx-auto mb-2" />
                      ) : (
                        <Camera className="h-8 w-8 mx-auto mb-2" />
                      )}
                      <p className="text-sm">{cameraResult.message}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Camera selector */}
              {cameras.length > 1 && (
                <DeviceSelector
                  label="Kamera wählen"
                  devices={cameras}
                  selected={selectedCamera}
                  onChange={(id) => {
                    setSelectedCamera(id);
                    saveDevicePreference("camera", id);
                    checkCamera(id);
                  }}
                />
              )}

              {/* Background Effects */}
              {cameraResult.status === "ok" && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Hintergrund</p>
                  <BackgroundPicker
                    mode={background.mode}
                    blurRadius={background.blurRadius}
                    selectedPreset={background.selectedPreset}
                    presets={presets}
                    isSupported={background.isSupported}
                    isProcessing={background.isProcessing}
                    onSelectNone={background.disable}
                    onSelectBlur={background.setBlur}
                    onSelectImage={background.setImage}
                    onBlurRadiusChange={(radius: number) => background.setBlur(radius)}
                  />
                </div>
              )}

              <p className="text-sm text-muted-foreground">{cameraResult.message}</p>
            </CardContent>
          </Card>

          {/* Microphone Check */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {micResult.status === "ok" ? (
                    <Mic className="h-5 w-5 text-green-600" />
                  ) : micResult.status === "error" ? (
                    <MicOff className="h-5 w-5 text-red-600" />
                  ) : (
                    <Mic className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <CardTitle className="text-lg">Mikrofon</CardTitle>
                    {micResult.deviceLabel && (
                      <CardDescription>{micResult.deviceLabel}</CardDescription>
                    )}
                  </div>
                </div>
                <StatusBadge status={micResult.status} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Audio level meter */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Pegel</span>
                  <span className="font-mono text-xs">{audioLevel}%</span>
                </div>
                <div className="h-4 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-75 ${
                      audioLevel > 60
                        ? "bg-green-500"
                        : audioLevel > 20
                          ? "bg-yellow-500"
                          : "bg-stone-400"
                    }`}
                    style={{ width: `${audioLevel}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Sprechen Sie, um den Eingangspegel zu testen
                </p>
              </div>

              {/* Mic selector */}
              {microphones.length > 1 && (
                <DeviceSelector
                  label="Mikrofon wählen"
                  devices={microphones}
                  selected={selectedMic}
                  onChange={(id) => {
                    setSelectedMic(id);
                    saveDevicePreference("microphone", id);
                    checkMicrophone(id);
                  }}
                />
              )}
              <p className="text-sm text-muted-foreground">{micResult.message}</p>
            </CardContent>
          </Card>

          {/* Speaker Check */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Volume2
                    className={`h-5 w-5 ${
                      speakerResult.status === "ok"
                        ? "text-green-600"
                        : speakerResult.status === "warning"
                          ? "text-amber-600"
                          : "text-muted-foreground"
                    }`}
                  />
                  <CardTitle className="text-lg">Lautsprecher</CardTitle>
                </div>
                <StatusBadge status={speakerResult.status} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                variant="outline"
                onClick={() => checkSpeaker(selectedSpeaker || undefined)}
                className="w-full gap-2"
              >
                <Volume2 className="h-4 w-4" />
                Testton abspielen
              </Button>

              {/* Speaker selector */}
              {speakers.length > 1 && (
                <DeviceSelector
                  label="Lautsprecher wählen"
                  devices={speakers}
                  selected={selectedSpeaker}
                  onChange={(id) => {
                    setSelectedSpeaker(id);
                    saveDevicePreference("speaker", id);
                    checkSpeaker(id);
                  }}
                />
              )}
              <p className="text-sm text-muted-foreground">{speakerResult.message}</p>
            </CardContent>
          </Card>
        </div>

        {/* Browser Compatibility */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Monitor className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg">Browser-Kompatibilität</CardTitle>
              </div>
              <StatusBadge status={browserResult.status} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <FeatureCheck
                label="getUserMedia"
                ok={!!navigator.mediaDevices?.getUserMedia}
              />
              <FeatureCheck
                label="WebRTC"
                ok={!!window.RTCPeerConnection}
              />
              <FeatureCheck
                label="MediaStream"
                ok={!!window.MediaStream}
              />
              <FeatureCheck
                label="Sicherer Kontext"
                ok={window.isSecureContext}
              />
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              {browserResult.message}
            </p>
          </CardContent>
        </Card>

        {/* Device Summary Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Erkannte Geräte</CardTitle>
            <CardDescription>
              {cameras.length + microphones.length + speakers.length} Geräte gefunden
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {cameras.length === 0 && microphones.length === 0 && speakers.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Keine Geräte erkannt. Bitte Zugriff erlauben und erneut prüfen.
                </p>
              )}
              {cameras.map((d, i) => (
                <DeviceRow
                  key={d.deviceId || `cam-${i}`}
                  icon={<Camera className="h-4 w-4" />}
                  label={d.label || `Kamera ${i + 1}`}
                  type="Kamera"
                />
              ))}
              {microphones.map((d, i) => (
                <DeviceRow
                  key={d.deviceId || `mic-${i}`}
                  icon={<Mic className="h-4 w-4" />}
                  label={d.label || `Mikrofon ${i + 1}`}
                  type="Mikrofon"
                />
              ))}
              {speakers.map((d, i) => (
                <DeviceRow
                  key={d.deviceId || `spk-${i}`}
                  icon={<Volume2 className="h-4 w-4" />}
                  label={d.label || `Lautsprecher ${i + 1}`}
                  type="Lautsprecher"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// --- Sub-components ---

function StatusBadge({ status }: { status: DeviceStatus }) {
  switch (status) {
    case "ok":
      return (
        <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-3 w-3 mr-1" /> OK
        </Badge>
      );
    case "error":
      return (
        <Badge variant="outline" className="text-red-600 border-red-300 bg-red-50 dark:bg-red-950/30">
          <XCircle className="h-3 w-3 mr-1" /> Fehler
        </Badge>
      );
    case "warning":
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <AlertTriangle className="h-3 w-3 mr-1" /> Warnung
        </Badge>
      );
    case "checking":
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50 dark:bg-blue-950/30">
          <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Prüfe...
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground">
          Ausstehend
        </Badge>
      );
  }
}

function FeatureCheck({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-red-600 shrink-0" />
      )}
      <span className={ok ? "" : "text-red-600"}>{label}</span>
    </div>
  );
}

function DeviceSelector({
  label,
  devices,
  selected,
  onChange,
}: {
  label: string;
  devices: MediaDeviceInfo[];
  selected: string;
  onChange: (deviceId: string) => void;
}) {
  return (
    <div className="relative">
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <div className="relative">
        <select
          value={selected}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-md border border-input bg-background px-3 py-2 pr-8 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          {devices.map((d, i) => (
            <option key={d.deviceId || i} value={d.deviceId}>
              {d.label || `Gerät ${i + 1}`}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>
    </div>
  );
}

function DeviceRow({
  icon,
  label,
  type,
}: {
  icon: React.ReactNode;
  label: string;
  type: string;
}) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="flex items-center gap-3">
        <span className="text-muted-foreground">{icon}</span>
        <span className="text-sm">{label}</span>
      </div>
      <span className="text-xs text-muted-foreground">{type}</span>
    </div>
  );
}
